import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle, Facebook, Star } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { SAP_LOGO_URL } from './SAPCompliance';
import { CookieSettingsButton } from './GDPRCookieConsent';
import LanguageSwitcher from './LanguageSwitcher';

const Footer = () => {
  const { contact, serviceArea } = siteConfig;
  
  // Key towns for footer links
  const keyTowns = ["Coëx", "Saint-Gilles-Croix-de-Vie", "Challans", "Brétignolles-sur-Mer", "Saint-Hilaire-de-Riez", "Aizenay"];
  
  const createSlug = (town) => {
    return town
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          
          {/* NAP & Business Info */}
          <div className="lg:col-span-2">
            <div className="inline-block bg-white/10 backdrop-blur-sm rounded-lg p-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_45bfe101-04b8-422c-9314-86de491da150/artifacts/hp6ix0ei_fulllogo_transparent_nobuffer.png" 
                alt="Le Rosebif - Jardinier Vendée" 
                className="h-12 w-auto"
                loading="lazy"
              />
            </div>
            
            <p className="text-sm mb-3 font-medium text-white">
              Jardinier à domicile — Basé à Coëx (85220)
            </p>
            
            <div className="space-y-2 text-sm mb-4">
              <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                <Phone size={16} className="text-lime-400" />
                {contact.phone}
              </a>
              <a href="https://wa.me/33782586685" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                <MessageCircle size={16} className="text-lime-400" />
                WhatsApp
              </a>
              <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                <Mail size={16} className="text-lime-400" />
                {contact.email}
              </a>
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-lime-400" />
                Déplacement 25–30 km
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-lime-400" />
                Lun, Mar, Jeu, Ven 11h–17h
              </div>
            </div>

            {/* SAP Badge with Logo */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <img 
                  src={SAP_LOGO_URL}
                  alt="Logo Services à la personne"
                  className="h-10 w-auto"
                  loading="lazy"
                />
                <div>
                  <p className="text-lime-400 font-semibold text-sm">Service à la personne</p>
                  <p className="text-xs text-gray-400">Éligible crédit d'impôt 50%</p>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Déclaration : {contact.sapAgreement}
              </p>
            </div>

            <Link to="/avance-immediate" className="text-xs text-blue-400 hover:text-blue-300 transition-colors block mb-4">
              Avance immédiate (option, si éligible) →
            </Link>
            
            {/* Payment & Zone Info */}
            <div className="space-y-1.5 text-xs text-gray-400 border-t border-gray-700 pt-3 mt-3">
              <p>Paiement : virement ou chèque. Facture TTC, paiement sous 15 jours.</p>
              <p>Zone : Coëx + 25–30 km (Brétignolles, Saint-Gilles, Givrand, Apremont, Aizenay…)</p>
            </div>

            {/* Social / official profiles */}
            <div className="border-t border-gray-700 pt-3 mt-3" data-testid="footer-social">
              <p className="text-white font-semibold text-sm mb-3">Suivez-moi</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={contact.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm transition-colors"
                  data-testid="footer-facebook-link"
                  aria-label="Le Rosebif sur Facebook"
                >
                  <Facebook size={16} className="text-lime-400" />
                  Facebook
                </a>
                <a
                  href="https://maps.app.goo.gl/3ssm3YBHfBfcYC8s5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm transition-colors"
                  data-testid="footer-google-link"
                  aria-label="Le Rosebif sur Google (fiche établissement et avis)"
                >
                  <Star size={16} className="text-lime-400" />
                  Fiche Google
                </a>
              </div>
            </div>
          </div>

          {/* Column 1: Prestations + Solutions jardin */}
          <div>
            <h3 className="text-white font-semibold mb-4">Prestations SAP</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services/tonte" className="hover:text-lime-400 transition-colors">Tonte de pelouse</Link></li>
              <li><Link to="/services/haies" className="hover:text-lime-400 transition-colors">Taille de haies</Link></li>
              <li><Link to="/services/debroussaillage" className="hover:text-lime-400 transition-colors">Débroussaillage</Link></li>
              <li><Link to="/services/vigilance" className="hover:text-lime-400 transition-colors">Vigilance résidence</Link></li>
              <li><Link to="/services/bricolage" className="hover:text-lime-400 transition-colors">Petit bricolage (≤ 2h)</Link></li>
              <li><Link to="/entretien-pelouse-robot-tondeuse" className="hover:text-lime-400 transition-colors">Finitions robot tondeuse</Link></li>
              <li><Link to="/tarifs" className="hover:text-lime-400 transition-colors font-medium">→ Tous les tarifs</Link></li>
            </ul>

            <h3 className="text-white font-semibold mb-4 mt-8">Solutions jardin</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/jardin-pret-arrivee-vendee" className="hover:text-lime-400 transition-colors">Jardin prêt avant arrivée</Link></li>
              <li><Link to="/jardinage-senior-vendee" className="hover:text-lime-400 transition-colors">Aide jardinage seniors</Link></li>
              <li><Link to="/entretien-massifs-desherbage-paillage" className="hover:text-lime-400 transition-colors">Massifs & désherbage</Link></li>
              <li><Link to="/jardin-secheresse-paillage-vendee" className="hover:text-lime-400 transition-colors">Jardin plus facile en été</Link></li>
              <li><Link to="/scarification-entretien-pelouse-vendee" className="hover:text-lime-400 transition-colors">Rénovation pelouse</Link></li>
            </ul>
          </div>

          {/* Column 2: Zones + Guides pratiques + Conseils saisonniers */}
          <div>
            <h3 className="text-white font-semibold mb-4">Zones d'intervention</h3>
            <ul className="space-y-2 text-sm">
              {keyTowns.map(town => (
                <li key={town}>
                  <Link to={`/zone/${createSlug(town)}`} className="hover:text-lime-400 transition-colors">
                    {town}
                  </Link>
                </li>
              ))}
              <li><Link to="/zone" className="hover:text-lime-400 transition-colors font-medium">→ 31 communes</Link></li>
            </ul>

            <h3 className="text-white font-semibold mb-4 mt-8">Guides pratiques</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/guide-gestion-residence-cotiere-vendee" className="hover:text-lime-400 transition-colors">Gestion résidence côtière</Link></li>
              <li><Link to="/entretien-residence-secondaire" className="hover:text-lime-400 transition-colors">Entretien résidence secondaire</Link></li>
              <li><Link to="/guide-jardinage-vendee" className="hover:text-lime-400 transition-colors">Guide jardinage Vendée</Link></li>
            </ul>

            <h3 className="text-white font-semibold mb-4 mt-8">Conseils saisonniers</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Link to="/entretien-jardin/printemps" className="hover:text-lime-400 transition-colors">Printemps</Link>
              <Link to="/entretien-jardin/ete" className="hover:text-lime-400 transition-colors">Été</Link>
              <Link to="/entretien-jardin/automne" className="hover:text-lime-400 transition-colors">Automne</Link>
              <Link to="/entretien-jardin/hiver" className="hover:text-lime-400 transition-colors">Hiver</Link>
            </div>
          </div>

          {/* Column 3: Informations + Problèmes courants */}
          <div>
            <h3 className="text-white font-semibold mb-4">Informations</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/devis" className="hover:text-lime-400 transition-colors font-medium text-lime-400">★ Devis gratuit</Link></li>
              <li><Link to="/mon-parcours" className="hover:text-lime-400 transition-colors">Mon parcours</Link></li>
              <li><Link to="/avance-immediate" className="hover:text-lime-400 transition-colors">Avance immédiate (SAP)</Link></li>
              <li><Link to="/faq" className="hover:text-lime-400 transition-colors">FAQ</Link></li>
              <li><Link to="/credit-impot-jardinage" className="hover:text-lime-400 transition-colors">Crédit d'impôt 50%</Link></li>
              <li><Link to="/contact" className="hover:text-lime-400 transition-colors">Contact</Link></li>
              <li><Link to="/blog" className="hover:text-lime-400 transition-colors">Blog & conseils jardin</Link></li>
              <li><a href="https://maps.app.goo.gl/3ssm3YBHfBfcYC8s5" target="_blank" rel="noopener noreferrer" className="hover:text-lime-400 transition-colors">⭐ Avis Google (5.0)</a></li>
            </ul>

            <h3 className="text-white font-semibold mb-4 mt-8">Prochainement</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/normandie" className="hover:text-lime-400 transition-colors">Jardinier à Caen (Normandie) — 2027</Link></li>
            </ul>

            <h3 className="text-white font-semibold mb-4 mt-8">Problèmes courants</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/jardin-en-friche" className="hover:text-lime-400 transition-colors">Jardin en friche</Link></li>
              <li><Link to="/haies-trop-hautes" className="hover:text-lime-400 transition-colors">Haies trop hautes</Link></li>
              <li><Link to="/terrain-abandonne-vendee" className="hover:text-lime-400 transition-colors">Terrain abandonné</Link></li>
            </ul>
          </div>
        </div>

        {/* English-speaking clients (discreet) */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <div className="max-w-2xl mx-auto text-center bg-white/5 border border-white/10 rounded-xl p-5" data-testid="footer-english-block">
            <h3 className="text-white font-semibold mb-1">English-speaking clients</h3>
            <p className="text-sm text-gray-400 mb-3">
              Vous êtes anglophone ou propriétaire d'une résidence secondaire en Vendée ? Le Rosebif peut aussi vous accompagner en anglais.
            </p>
            <Link to="/en/" className="inline-flex items-center gap-1.5 text-lime-400 hover:text-lime-300 transition-colors text-sm font-medium" data-testid="footer-english-link">
              English-speaking gardener in Vendée →
            </Link>
          </div>
        </div>

        {/* SAP Compliance Section */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <div className="text-center mb-4">
            <p className="text-sm text-emerald-400 font-medium mb-2">
              Service à la personne – éligible au crédit d'impôt de 50% (selon conditions fiscales)
            </p>
            <p className="text-xs text-gray-500">
              <span className="text-gray-400 font-medium">Hors périmètre SAP :</span>{' '}
              Plomberie • Électricité • Chauffage/gaz • Gros travaux/bâtiment • Élagage à risque • Débarras/déménagement • Traitements phytosanitaires interdits
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6">
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm mb-4">
            <Link to="/mentions-legales" className="hover:text-lime-400 transition-colors">Mentions légales</Link>
            <span className="text-gray-600">|</span>
            <Link to="/politique-confidentialite" className="hover:text-lime-400 transition-colors">Confidentialité</Link>
            <span className="text-gray-600">|</span>
            <Link to="/cookies" className="hover:text-lime-400 transition-colors">Cookies</Link>
            <span className="text-gray-600">|</span>
            <CookieSettingsButton className="text-gray-300 hover:text-lime-400" />
            <span className="text-gray-600">|</span>
            <Link to="/conditions-utilisation" className="hover:text-lime-400 transition-colors">CGU</Link>
            <span className="text-gray-600">|</span>
            <LanguageSwitcher className="text-gray-300" />
          </div>
          
          <div className="text-center text-sm">
            <p className="mb-2 font-medium text-white">
              © {new Date().getFullYear()} {siteConfig.businessName} — Tous droits réservés
            </p>
            <p className="text-xs text-emerald-400 mb-2">
              Météo Jardin™ est une création exclusive de Le Rosebif
            </p>
            <p className="text-xs text-gray-500">
              SIRET : {contact.siret} | Déclaration SAP : {contact.sapAgreement} | {contact.insurance}
            </p>
            <p className="text-xs text-gray-600 mt-3">
              Reproduction interdite sans autorisation écrite préalable.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
